import { Text } from '@noorden-insurtech/alba-design-system';
import type { PmeDemonstrativeRow } from '../../proposal.mock';
import type { PmeCommercializationRow } from '../../utils/hydrate-proposal.util';

export function PmeDemonstrativeSection({
  rows,
  isRecalculating = false,
}: {
  rows: PmeDemonstrativeRow[];
  /**
   * Recálculo em andamento: os VALORES ficam indisponíveis (mesmo tratamento
   * do card de prêmio). A descrição da cobertura continua legível — só os
   * números é que estão desatualizados enquanto o cálculo roda.
   */
  isRecalculating?: boolean;
}) {
  const blurValueClass = isRecalculating ? 'blur-[6px] select-none' : '';

  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-base text-alba-black-500'>Demonstrativo</Text>
      <div className='overflow-hidden rounded-xl border border-alba-gray-300'>
        <div className='grid grid-cols-[2fr_1fr_1fr_1fr] bg-alba-gray-100 px-4 py-3'>
          <Text className='text-xs font-bold text-alba-black-500'>
            Descrição
          </Text>
          <Text className='text-right text-xs font-bold text-alba-black-500'>
            Valor
          </Text>
          <Text className='text-right text-xs font-bold text-alba-black-500'>
            IOF
          </Text>
          <Text className='text-right text-xs font-bold text-alba-black-500'>
            Total
          </Text>
        </div>
        {rows.map((row, index) => (
          <div
            key={row.description}
            className={`grid grid-cols-[2fr_1fr_1fr_1fr] px-4 py-3 ${
              index > 0 ? 'border-t border-alba-gray-200' : ''
            }`}
          >
            <Text className='text-sm text-alba-black-500'>
              {row.description}
            </Text>
            <Text
              className={`text-right text-sm text-alba-gray-600 ${blurValueClass}`}
            >
              {row.value}
            </Text>
            <Text
              className={`text-right text-sm text-alba-gray-600 ${blurValueClass}`}
            >
              {row.iof}
            </Text>
            <Text
              className={`text-right text-sm text-alba-gray-600 ${blurValueClass}`}
            >
              {row.total}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PmeCommercializationSection({
  rows,
  compact = false,
  showUnderwritingFields = false,
  surcharge,
  actuarialDiscount,
}: {
  rows: PmeCommercializationRow[];
  compact?: boolean;
  showUnderwritingFields?: boolean;
  surcharge?: string;
  actuarialDiscount?: string;
}) {
  const percentages = [
    ...rows,
    ...(showUnderwritingFields
      ? [
          { label: 'Agravo', value: `${surcharge?.trim() || '0,00'} %` },
          {
            label: 'Desconto atuarial',
            value: `${actuarialDiscount?.trim() || '0,00'} %`,
          },
        ]
      : []),
  ];

  if (compact) {
    return (
      <div className='flex flex-col gap-4'>
        <Text className='text-base text-alba-black-500'>Comercialização</Text>
        <div
          className={`grid gap-6 ${
            showUnderwritingFields
              ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
              : 'grid-cols-1 sm:grid-cols-2'
          }`}
        >
          {percentages.map(row => (
            <div key={row.label} className='flex flex-col gap-1'>
              <Text className='text-sm font-bold text-alba-black-500'>
                {row.label === 'Corretagem'
                  ? 'Percentual da Corretagem'
                  : row.label === 'Agenciamento'
                    ? 'Percentual do Agenciamento'
                    : row.label}
              </Text>
              <Text className='text-sm text-alba-gray-600'>{row.value}</Text>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-base text-alba-black-500'>Comercialização</Text>
      <div className='flex flex-col gap-2 rounded-xl border border-alba-gray-300 p-4'>
        <Text className='text-sm font-bold text-alba-black-500'>
          Percentual
        </Text>
        <div className='h-px w-full bg-alba-gray-200' />
        {percentages.map(row => (
          <div
            key={row.label}
            className='flex items-center justify-between gap-4'
          >
            <Text className='text-sm text-alba-gray-600'>{row.label}</Text>
            <Text className='text-sm text-alba-gray-600'>{row.value}</Text>
          </div>
        ))}
      </div>
    </div>
  );
}
